import { useTranslation } from "react-i18next";
import type { Summary, SummaryAction } from "@/types";
import { colors } from "@/constants/theme";

interface SummaryViewProps {
  summary: Summary;
}

function ActionItem({ action }: { action: SummaryAction }) {
  return (
    <div className="flex items-start gap-2 mb-2">
      <svg
        className="w-4 h-4 mt-0.5 shrink-0"
        fill={colors.primary}
        viewBox="0 0 24 24"
      >
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
      <div>
        <p className="text-redi-text text-sm">{action.what}</p>
        <p className="text-redi-muted text-xs">
          {action.who}
          {action.deadline ? ` · ${action.deadline}` : ""}
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  items,
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-4">
      <h4 className="text-redi-primary text-sm font-semibold mb-2">{title}</h4>
      {items.map((item, i) => (
        <p key={i} className="text-redi-text text-sm ml-4 mb-1">
          &bull; {item}
        </p>
      ))}
    </div>
  );
}

function TextBlock({
  title,
  value,
}: {
  title: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <div className="mb-4">
      <h4 className="text-redi-primary text-sm font-semibold mb-2">{title}</h4>
      <p className="text-redi-text text-sm ml-4 leading-5">{value}</p>
    </div>
  );
}

function VoteItem({
  vote,
}: {
  vote: { subject: string; result: string; details?: string };
}) {
  return (
    <div className="flex items-start gap-2 mb-2 ml-4">
      <span className="text-redi-primary">&#9758;</span>
      <div>
        <p className="text-redi-text text-sm">{vote.subject}</p>
        <p className="text-redi-muted text-xs">
          {vote.result}
          {vote.details ? ` — ${vote.details}` : ""}
        </p>
      </div>
    </div>
  );
}

function getStringArray(
  content: Record<string, unknown>,
  key: string
): string[] {
  const val = content[key];
  if (Array.isArray(val))
    return val.filter((v): v is string => typeof v === "string");
  return [];
}

function getString(
  content: Record<string, unknown>,
  key: string
): string | null {
  const val = content[key];
  return typeof val === "string" ? val : null;
}

function getExtra(content: Record<string, unknown>, key: string): unknown {
  return content[key];
}

function LawyerSections({ content }: { content: Record<string, unknown> }) {
  const { t } = useTranslation();
  return (
    <>
      <TextBlock
        title={t("summary.client")}
        value={getString(content, "client_name")}
      />
      <Section
        title={t("summary.legalPoints")}
        items={getStringArray(content, "legal_points")}
      />
      <Section
        title={t("summary.legalReferences")}
        items={getStringArray(content, "legal_references")}
      />
      <Section
        title={t("summary.clientInstructions")}
        items={getStringArray(content, "client_instructions")}
      />
      <TextBlock
        title={t("summary.confidentiality")}
        value={getString(content, "confidentiality_note")}
      />
    </>
  );
}

function ConsultantSections({
  content,
}: {
  content: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <>
      <TextBlock
        title={t("summary.missionContext")}
        value={getString(content, "context")}
      />
      <Section
        title={t("summary.recommendations")}
        items={getStringArray(content, "recommendations")}
      />
      <Section
        title={t("summary.risksIdentified")}
        items={getStringArray(content, "risks_identified")}
      />
      <Section
        title={t("summary.deliverables")}
        items={getStringArray(content, "deliverables")}
      />
      <Section
        title={t("summary.openQuestions")}
        items={getStringArray(content, "open_questions")}
      />
    </>
  );
}

function AssociationSections({
  content,
}: {
  content: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const attendees = content["attendees"] as
    | { present?: string[]; absent_excused?: string[] }
    | undefined;
  const votes = getExtra(content, "votes") as
    | { subject: string; result: string; details?: string }[]
    | undefined;

  return (
    <>
      <TextBlock
        title={t("summary.meetingType")}
        value={getString(content, "meeting_type")}
      />
      <TextBlock
        title={t("summary.quorum")}
        value={getString(content, "quorum")}
      />
      <Section
        title={t("summary.agenda")}
        items={getStringArray(content, "agenda")}
      />
      {attendees?.present && attendees.present.length > 0 && (
        <Section title={t("summary.present")} items={attendees.present} />
      )}
      {attendees?.absent_excused && attendees.absent_excused.length > 0 && (
        <Section
          title={t("summary.absentExcused")}
          items={attendees.absent_excused}
        />
      )}
      {votes && votes.length > 0 && (
        <div className="mb-4">
          <h4 className="text-redi-primary text-sm font-semibold mb-2">
            {t("summary.votes")}
          </h4>
          {votes.map((vote, i) => (
            <VoteItem key={i} vote={vote} />
          ))}
        </div>
      )}
      <Section
        title={t("summary.resolutions")}
        items={getStringArray(content, "resolutions")}
      />
      <TextBlock
        title={t("summary.nextMeeting")}
        value={getString(content, "next_meeting")}
      />
    </>
  );
}

function CommercialSections({
  content,
}: {
  content: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const interestLevel = getString(content, "interest_level");
  const interestColors: Record<string, string> = {
    high: colors.success,
    medium: colors.warning,
    low: colors.error,
  };
  const interestLabels: Record<string, string> = {
    high: t("summary.interestHigh"),
    medium: t("summary.interestMedium"),
    low: t("summary.interestLow"),
  };

  return (
    <>
      <TextBlock
        title={t("summary.prospect")}
        value={getString(content, "prospect_company")}
      />
      {interestLevel && (
        <div className="mb-4">
          <h4 className="text-redi-primary text-sm font-semibold mb-2">
            {t("summary.interestLevel")}
          </h4>
          <div className="flex items-center gap-2 ml-4">
            <span
              className="w-2.5 h-2.5 rounded-full inline-block"
              style={{
                backgroundColor:
                  interestColors[interestLevel] ?? colors.muted,
              }}
            />
            <span className="text-redi-text text-sm capitalize">
              {interestLabels[interestLevel] ?? interestLevel}
            </span>
          </div>
        </div>
      )}
      <Section
        title={t("summary.needsExpressed")}
        items={getStringArray(content, "needs_expressed")}
      />
      <Section
        title={t("summary.objections")}
        items={getStringArray(content, "objections")}
      />
      <TextBlock
        title={t("summary.budgetMentioned")}
        value={getString(content, "budget_mentioned")}
      />
      <TextBlock
        title={t("summary.timeline")}
        value={getString(content, "timeline_mentioned")}
      />
      <Section
        title={t("summary.competitorsMentioned")}
        items={getStringArray(content, "competitors_mentioned")}
      />
      <TextBlock
        title={t("summary.dealNotes")}
        value={getString(content, "deal_notes")}
      />
    </>
  );
}

export function SummaryView({ summary }: SummaryViewProps) {
  const { t } = useTranslation();
  const { content, template_key } = summary;
  const raw = content as unknown as Record<string, unknown>;

  return (
    <div className="mb-6">
      <h3 className="text-redi-text text-lg font-semibold mb-3">
        {t("summary.title")}
      </h3>
      <div className="bg-redi-surface rounded-xl p-5">
        {content.overview && (
          <p className="text-redi-text text-sm mb-4 leading-5">
            {content.overview}
          </p>
        )}

        {template_key === "avocat" && <LawyerSections content={raw} />}
        {template_key === "consultant" && (
          <ConsultantSections content={raw} />
        )}
        {template_key === "association" && (
          <AssociationSections content={raw} />
        )}
        {template_key === "commercial" && (
          <CommercialSections content={raw} />
        )}

        <Section title={t("summary.decisions")} items={content.decisions} />
        <Section
          title={t("summary.nextSteps")}
          items={content.next_steps}
        />

        {content.actions.length > 0 && (
          <div className="mb-4">
            <h4 className="text-redi-primary text-sm font-semibold mb-2">
              {t("summary.actions")}
            </h4>
            {content.actions.map((action, i) => (
              <ActionItem key={i} action={action} />
            ))}
          </div>
        )}

        {content.key_quotes && content.key_quotes.length > 0 && (
          <Section
            title={t("summary.keyQuotes")}
            items={content.key_quotes}
          />
        )}

        {content.participants.length > 0 && (
          <p className="text-redi-muted text-xs mt-2">
            {t("summary.participants")} : {content.participants.join(", ")}
          </p>
        )}
      </div>
    </div>
  );
}
